import create from 'zustand';
import axios from 'axios';
import { API_URL } from '../config';

const AUTH_TOKEN_KEY = 'token';

function readStoredToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function persistToken(token) {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

function clearStoredToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

function userFromToken(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.userId || payload.id,
      email: payload.email,
      name: payload.name || payload.email
    };
  } catch {
    return null;
  }
}

export const useAuthStore = create((set) => ({
  user: userFromToken(readStoredToken()),
  token: readStoredToken(),
  isLoading: false,
  error: null,

  signup: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post(`${API_URL}/auth/signup`, { email, password });
      const token = response.data.token;
      persistToken(token);
      set({ user: response.data.user, token, isLoading: false });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Signup failed';
      set({ error: errorMsg, isLoading: false });
      throw error;
    }
  },

  login: async (username, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        email: username,
        password
      });
      const token = response.data.token;
      persistToken(token);
      set({ user: response.data.user, token, isLoading: false });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Login failed';
      set({ error: errorMsg, isLoading: false });
      throw error;
    }
  },

  logout: () => {
    clearStoredToken();
    set({ user: null, token: null });
  },

  checkToken: async () => {
    const token = readStoredToken();
    if (!token) {
      set({ token: null, user: null });
      return;
    }

    const cached = userFromToken(token);
    set({ token, user: cached });

    try {
      const response = await axios.post(
        `${API_URL}/auth/verify`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      set({
        user: {
          id: response.data.user.userId || response.data.user.id,
          email: response.data.user.email,
          name: response.data.user.name || response.data.user.email
        },
        token
      });
    } catch (error) {
      if (error.response?.status === 401) {
        clearStoredToken();
        set({ token: null, user: null });
      }
    }
  }
}));
